import React from 'react';
import { Link } from 'react-router-dom';
import { Popover, Tooltip } from 'antd';
import styles from './styles.module.css';

interface SubMenuProps {
  title: string;
  paths: string[];
  labels: string[];
  collapsed: boolean;
  isPopoverVisible: boolean;
  openKeys: string[];
  currentPath: string;
  subMenuKey: string;
  onTogglePopover: () => void;
  onOpenChange: (key: string) => void;
  onLinkClick: (key: string) => void;
  setPopoverVisible: (visible: boolean) => void;
  icon: string;
  disabled?: boolean;
}

export const SubMenu: React.FC<SubMenuProps> = ({
  title,
  paths,
  labels,
  collapsed,
  isPopoverVisible,
  openKeys,
  currentPath,
  subMenuKey,
  onTogglePopover,
  onOpenChange,
  onLinkClick,
  setPopoverVisible,
  icon,
  disabled = false,
}) => {
  const isActive = paths.some(path => currentPath.startsWith(path));

  const renderMenuItems = () => (
    paths.map((path, index) => (
      <li
        key={index}
        className={`${styles.popoverMenuItem} ${
          currentPath === path ? styles.activeLink : ""
        }`}
      >
        <Link
          className={styles.popoverMenuLink}
          to={path}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            onLinkClick(subMenuKey);
            setPopoverVisible(false);
          }}
        >
          {labels[index]}
        </Link>
      </li>
    ))
  );

  const menuContent = (
    <div className="resizablePopover">
      <div className={styles.popoverTitle}>{title}</div>
      <ul className={styles.popoverMenu}>
        {renderMenuItems()}
      </ul>
    </div>
  );

  return (
    <li
      className={`${styles.menuItem} ${styles.resizableMenuItem} ${
        isActive ? styles.active : ""
      } ${disabled ? styles.disabled : ""}`}
    >
      <div
        className={`${styles.menuLink} ${isActive ? styles.activeButton : ""}`}
        onClick={() => {
          if (disabled) return;
          if (collapsed) {
            onTogglePopover();
          } else {
            onOpenChange(subMenuKey);
          }
        }}
      >
        <div className={`${styles.menuLink_inner} ${styles.resizableLinkInner}`}>
          {collapsed ? (
            <Tooltip title={title} placement="right">
              <Popover
                placement="right"
                content={menuContent}
                trigger="click"
                visible={!disabled && collapsed && isPopoverVisible}
                onVisibleChange={(visible) => !disabled && setPopoverVisible(visible)}
                className="resizablePopover"
              >
                <div className={styles.icons_inner}>
                  <img src={icon} alt="" />
                </div>
              </Popover>
            </Tooltip>
          ) : (
            <>
              <div className={styles.icons_inner}>
                <img src={icon} alt="" />
              </div>
              <span className={`${styles.resizableText} ${isActive ? styles.activeText : ""}`}>
                {title}
              </span>
              <img
                src="/chevron.svg"
                alt="arrow"
                className={`${styles.arrowIcon} ${
                  openKeys.includes(subMenuKey) ? styles.arrowOpen : ""
                }`}
              />
            </>
          )}
        </div>
      </div>

      {!collapsed && !disabled && (
        <ul
          className={`${styles.subMenu} ${
            openKeys.includes(subMenuKey) ? styles.open : ""
          }`}
        >
          {paths.map((path, index) => (
            <li
              key={index}
              className={`${styles.subMenuItem} ${
                currentPath === path ? styles.activeLink : ""
              }`}
            >
              <Link
                className={`${styles.menuLink} ${styles.resizableMenuLink}`}
                to={path}
                onClick={() => onLinkClick(subMenuKey)}
              >
                {labels[index]}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};
